import * as z from 'zod';
import { emptyPrice } from '@noor/ui';
import { promotionSchema } from '@/entities/promotion';
import { menuCategorySchema, menuProductSchema } from '@/entities/menu';

/** Translator shape (i18next's `t`), passed in so validation messages localize. */
type Translate = (key: string) => string;

// Modal payloads, validated at the modal registry boundary. `branches` seeds the
// branch multi-select; `categories`/`products` seed the product multi-select
// (the popup lists products, optionally narrowed by category). The `mode`
// discriminant lets one modal component serve both add and edit.

/**
 * A selectable branch. Carries the id because a promotion is created against
 * `restaurant_id`; the name is only what the merchant sees.
 */
export const promotionBranchSchema = z.object({
  id: z.string(),
  name: z.string(),
  /**
   * Street address, shown under the name in the wizard's branch list (Figma
   * 840-27485). Optional — several branches of one brand share a name
   * («Safia», «Safia»), so the address is often the only thing telling them
   * apart, but a branch whose address the scope did not resolve still renders
   * as a name-only row rather than a blank line.
   */
  address: z.string().optional(),
});
export type PromotionBranch = z.infer<typeof promotionBranchSchema>;

export const addPromotionPayloadSchema = z.object({
  mode: z.literal('add'),
  branches: z.array(promotionBranchSchema),
  categories: z.array(menuCategorySchema),
  products: z.array(menuProductSchema),
});
export type AddPromotionPayload = z.infer<typeof addPromotionPayloadSchema>;

export const editPromotionPayloadSchema = z.object({
  mode: z.literal('edit'),
  branches: z.array(promotionBranchSchema),
  products: z.array(menuProductSchema),
  promotion: promotionSchema,
});
export type EditPromotionPayload = z.infer<typeof editPromotionPayloadSchema>;

/** The two discount kinds offered by the «Тип скидки» select. */
export const DISCOUNT_TYPES = ['percent', 'fixed'] as const;
export type DiscountType = (typeof DISCOUNT_TYPES)[number];

/** Character ceiling for the promotion's name, matching the product form's. */
export const PROMOTION_NAME_MAX_LENGTH = 100;

/**
 * Per-branch product selection: `{ [restaurantId]: productId[] }`.
 *
 * The shape the wizard's «Филиал продукта» step collects and the wire's
 * `selections` body wants — a branch and the products the promotion covers
 * THERE. A flat product list could not express "this product only at that
 * branch", which is exactly what the redesign added.
 *
 * Branches with no products checked are kept as empty arrays while the merchant
 * works and dropped at submit; the refinement below is what requires at least
 * one branch to carry something.
 */
export const branchSelectionsSchema = z.record(z.string(), z.array(z.string()));
export type BranchSelections = z.infer<typeof branchSelectionsSchema>;

/** Every product id checked across all branches, de-duplicated. */
export function selectedProductIds(selections: BranchSelections): string[] {
  return [...new Set(Object.values(selections).flat())];
}

/** Branch ids that actually carry at least one checked product. */
export function selectedBranchIds(selections: BranchSelections): string[] {
  return Object.entries(selections)
    .filter(([, productIds]) => productIds.length > 0)
    .map(([branchId]) => branchId);
}

/**
 * Build the create/edit-promotion form schema with localized messages.
 *
 * A promotion applies a discount to products chosen PER BRANCH (see
 * {@link branchSelectionsSchema}). `discountType` picks how `discountValue` is
 * read — a percentage (0–100) or a fixed final price in so'm.
 *
 * Every promotion is DATED: both ends of the window are required. The
 * «Бессрочно» / open-ended escape hatch was removed — a promotion with no
 * expiry never stops discounting, which is not something a merchant can
 * currently undo from this panel.
 */
export function buildPromotionFormSchema(t: Translate) {
  return (
    z
      .object({
        /**
         * The campaign's own name, in all three platform languages — step 1 of
         * the wizard. Ru is required and Uz/En are not, the same rule the
         * category rename applies: a promotion may genuinely not be translated
         * yet, and the backend reads a blank as "not provided" rather than
         * storing an empty string.
         */
        nameRu: z
          .string()
          .trim()
          .min(1, t('promotions.form.nameRequired'))
          .max(PROMOTION_NAME_MAX_LENGTH, t('promotions.form.nameTooLong')),
        nameUz: z
          .string()
          .trim()
          .max(PROMOTION_NAME_MAX_LENGTH, t('promotions.form.nameTooLong')),
        nameEn: z
          .string()
          .trim()
          .max(PROMOTION_NAME_MAX_LENGTH, t('promotions.form.nameTooLong')),
        /** Products chosen per branch — step 2. */
        selections: branchSelectionsSchema,
        discountType: z.enum(DISCOUNT_TYPES),
        // `emptyPrice` collapses the cleared box's `null` to `undefined` so it
        // reads as "no value given" and hits the required message, rather than
        // a raw type complaint. See `FormField.Price`.
        discountValue: emptyPrice(
          z
            .number({ message: t('promotions.form.discountRequired') })
            .int()
            .positive(t('promotions.form.discountPositive')),
        ),
        startDate: z.date({ message: t('promotions.form.datesRequired') }),
        endDate: z.date({ message: t('promotions.form.datesRequired') }),
      })
      // At least one branch must carry at least one product — otherwise the
      // promotion applies to nothing. Reported on `selections` so the message
      // lands on step 2's picker rather than on the form root.
      .refine((v) => selectedBranchIds(v.selections).length > 0, {
        message: t('promotions.form.productRequired'),
        path: ['selections'],
      })
      // A percentage discount is capped at 100%.
      .refine((v) => v.discountType !== 'percent' || v.discountValue <= 100, {
        message: t('promotions.form.discountRange'),
        path: ['discountValue'],
      })
      // The end must not precede the start.
      .refine((v) => v.endDate >= v.startDate, {
        message: t('promotions.form.dateOrder'),
        path: ['endDate'],
      })
  );
}

export type PromotionFormValues = z.infer<ReturnType<typeof buildPromotionFormSchema>>;
